import { isCancelled } from '@/helpers';
import { _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { isClosed } from '@/support/study';

import type { OverviewSectionKey } from './OverviewAccordion.model';

/**
 * Mirrors StudyNav: Treatment groups is only offered while a study is in
 * progress or awaiting review, and never once it is cancelled.
 */
const treatmentGroupsAvailable = (study: Study): boolean => {
  if (isCancelled(study)) {
    return false;
  }

  return study.status === 'in_progress' || study.status === 'awaiting_approval';
};

/**
 * Same gate as the study-groups label: a write user on an editable study.
 * Diseases also need the disease-induction feature, because that tab is absent without it.
 */
export const canEditOverviewTreatment = (
  study: Study | undefined,
  type: 'treatment' | 'disease_induction',
  diseaseInduction: boolean
): boolean => {
  if (!_notNil(study) || !treatmentGroupsAvailable(study) || !Auth.isWriteUserForStudy(study)) {
    return false;
  }

  if (type === 'disease_induction') {
    return diseaseInduction === true;
  }

  return true;
};

/**
 * Same people who can edit task specs in Settings, and only while the study is open.
 */
const canEditOpenStudy = (study: Study | undefined): boolean =>
  _notNil(study) &&
  !isClosed(study) &&
  (Auth.isWriteUserForStudy(study) || Auth.isCurrentTeamOwnerWithSettingsAccess());

export const canUpdateOverviewTask = canEditOpenStudy;

export const canUpdateOverviewAlert = canEditOpenStudy;

/**
 * Whether Manage may link to the page that owns this section. Each destination
 * keeps its own access rule: study-groups write, samples write, or settings
 * (write, including a completed study, or a team owner allowed into settings).
 */
export const canManageOverviewSection = (
  sectionKey: OverviewSectionKey,
  study: Study | undefined,
  diseaseInduction: boolean
): boolean => {
  if (!_notNil(study)) {
    return false;
  }

  switch (sectionKey) {
    case 'groups':
    case 'treatments':
      return treatmentGroupsAvailable(study) && Auth.isWriteUserForStudy(study);
    case 'diseases':
      return diseaseInduction === true && treatmentGroupsAvailable(study) && Auth.isWriteUserForStudy(study);
    case 'sampling_plan':
      return Auth.isWriteUserForStudy(study);
    case 'metadata':
    case 'tasks':
    case 'preset':
    case 'alerts':
      return Auth.isWriteUserForStudy(study, false, false) || Auth.isCurrentTeamOwnerWithSettingsAccess();
  }
};
